import type { SupabaseClient } from "@supabase/supabase-js";

// Sends the Supabase Auth invite email for a pending membership invite.
// authorization is the caller's JWT (functions.invoke). The Edge Function uses service-role
// Admin only after proving the invite is visible on management_member_invites.

export const MEMBER_AUTH_INVITE_FUNCTION = "member-auth-invite";

export const SAFE_MEMBER_AUTH_INVITE_FAILURE =
  "Membership was reserved, but we could not email a sign-in invite. Use Resend sign-in email.";

/** Exact Edge local-budget sentence (contract.ts). Upstream Auth 429 uses a different message. */
export const LOCAL_AUTH_MAIL_BUDGET_MESSAGE =
  "Invite rate limit exceeded. Try again later.";

export const LOCAL_AUTH_MAIL_BUDGET_UI_REASON =
  "Reserved; sign-in mail not queued. The sign-in mail budget for this hour is used.";

export type MemberAuthInviteFailureKind = "local_auth_mail_budget" | "generic";

export type MemberAuthInviteSendOutcome =
  | { ok: true; kind: "invite_email_sent" | "already_registered" }
  | { ok: false; reason: string; failureKind: MemberAuthInviteFailureKind };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function extractAllowlistedErrorMessage(payload: unknown): string | null {
  if (typeof payload === "string" && payload.trim() === LOCAL_AUTH_MAIL_BUDGET_MESSAGE) {
    return LOCAL_AUTH_MAIL_BUDGET_MESSAGE;
  }
  if (!isRecord(payload)) return null;
  const error = payload.error;
  if (typeof error === "string" && error.trim() === LOCAL_AUTH_MAIL_BUDGET_MESSAGE) {
    return LOCAL_AUTH_MAIL_BUDGET_MESSAGE;
  }
  const message = payload.message;
  if (typeof message === "string" && message.trim() === LOCAL_AUTH_MAIL_BUDGET_MESSAGE) {
    return LOCAL_AUTH_MAIL_BUDGET_MESSAGE;
  }
  return null;
}

/**
 * G-INV-06: only the exact local-budget Edge sentence proves account/address hourly budget.
 * HTTP 429 alone, upstream Auth throttling copy, or missing body stay generic.
 */
export function classifyMemberAuthInviteFailure(input: {
  data: unknown;
  error: unknown;
  trustedErrorBody?: unknown;
}): Extract<MemberAuthInviteSendOutcome, { ok: false }> {
  const fromData = extractAllowlistedErrorMessage(input.data);
  if (fromData !== null) {
    return {
      ok: false,
      failureKind: "local_auth_mail_budget",
      reason: LOCAL_AUTH_MAIL_BUDGET_UI_REASON,
    };
  }
  const fromTrusted = extractAllowlistedErrorMessage(input.trustedErrorBody);
  if (fromTrusted !== null) {
    return {
      ok: false,
      failureKind: "local_auth_mail_budget",
      reason: LOCAL_AUTH_MAIL_BUDGET_UI_REASON,
    };
  }
  // Never classify from arbitrary error.message (may be FunctionsHttpError wrapper text).
  return {
    ok: false,
    failureKind: "generic",
    reason: SAFE_MEMBER_AUTH_INVITE_FAILURE,
  };
}

/** Maps Edge Function JSON to a UI-safe outcome. Never echoes server error detail to the form. */
export function mapMemberAuthInviteResponse(data: unknown): MemberAuthInviteSendOutcome {
  if (!isRecord(data)) {
    return {
      ok: false,
      failureKind: "generic",
      reason: SAFE_MEMBER_AUTH_INVITE_FAILURE,
    };
  }
  if (data.kind !== "member-auth-invite") {
    // Error envelope may land in `data` on some clients: { error: "Invite rate limit..." }
    return classifyMemberAuthInviteFailure({ data, error: null });
  }
  if (data.outcome === "invite_email_sent" || data.outcome === "already_registered") {
    return { ok: true, kind: data.outcome };
  }
  return {
    ok: false,
    failureKind: "generic",
    reason: SAFE_MEMBER_AUTH_INVITE_FAILURE,
  };
}

async function readTrustedErrorBody(error: unknown): Promise<unknown> {
  if (!isRecord(error)) return null;
  const context = error.context;
  if (context === null || context === undefined) return null;
  // supabase-js FunctionsHttpError.context is a Response with the Edge body.
  if (typeof (context as { json?: unknown }).json === "function") {
    try {
      return await (context as Response).json();
    } catch {
      return null;
    }
  }
  return null;
}

export async function sendMemberAuthInviteEmail(
  client: SupabaseClient,
  accountId: string,
  inviteId: string,
  redirectTo: string,
): Promise<MemberAuthInviteSendOutcome> {
  try {
    const { data, error } = await client.functions.invoke(MEMBER_AUTH_INVITE_FUNCTION, {
      body: {
        accountId,
        inviteId,
        redirectTo,
      },
    });
    if (error !== null) {
      const trustedErrorBody = await readTrustedErrorBody(error);
      return classifyMemberAuthInviteFailure({ data, error, trustedErrorBody });
    }
    return mapMemberAuthInviteResponse(data);
  } catch {
    return {
      ok: false,
      failureKind: "generic",
      reason: SAFE_MEMBER_AUTH_INVITE_FAILURE,
    };
  }
}

export function successNoteForAuthInvite(
  outcome: Extract<MemberAuthInviteSendOutcome, { ok: true }>,
): string {
  if (outcome.kind === "already_registered") {
    return "Membership is reserved. That email already has a sign-in account. They can use Login code or password.";
  }
  return "Sign-in email requested. Membership stays reserved until they join with the invited address.";
}
